import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Plus, Trash2, Edit, Save, X, Settings } from 'lucide-react';

const DEFAULT_SLABS = [
  { id: 1, minEnrollments: 0, maxEnrollments: 5, rate: 3, description: 'Starter' },
  { id: 2, minEnrollments: 6, maxEnrollments: 15, rate: 10, description: 'Bronze' },
  { id: 3, minEnrollments: 16, maxEnrollments: 30, rate: 15, description: 'Silver' },
  { id: 4, minEnrollments: 31, maxEnrollments: 50, rate: 20, description: 'Gold' },
  { id: 5, minEnrollments: 51, maxEnrollments: 100, rate: 25, description: 'Platinum' },
  { id: 6, minEnrollments: 101, maxEnrollments: null, rate: 50, description: 'Diamond' }
];

export default function CommissionSlabManager({ onSlabsChange, initialSlabs = DEFAULT_SLABS }) {
  const [slabs, setSlabs] = useState(initialSlabs);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [newSlab, setNewSlab] = useState({
    minEnrollments: '',
    maxEnrollments: '',
    rate: '',
    description: ''
  });

  const handleEdit = (slab) => {
    setEditingId(slab.id);
    setEditForm({
      minEnrollments: slab.minEnrollments,
      maxEnrollments: slab.maxEnrollments || '',
      rate: slab.rate,
      description: slab.description
    });
  };

  const handleSave = (id) => {
    const updatedSlabs = slabs.map(slab => 
      slab.id === id 
        ? {
            ...slab,
            minEnrollments: parseInt(editForm.minEnrollments),
            maxEnrollments: editForm.maxEnrollments ? parseInt(editForm.maxEnrollments) : null,
            rate: parseFloat(editForm.rate),
            description: editForm.description
          }
        : slab
    );
    setSlabs(updatedSlabs);
    setEditingId(null);
    setEditForm({});
    onSlabsChange?.(updatedSlabs);
  };

  const handleDelete = (id) => {
    const updatedSlabs = slabs.filter(slab => slab.id !== id);
    setSlabs(updatedSlabs);
    onSlabsChange?.(updatedSlabs);
  };

  const handleAddNew = () => {
    const newId = Math.max(...slabs.map(s => s.id)) + 1;
    const newSlabData = {
      id: newId,
      minEnrollments: parseInt(newSlab.minEnrollments),
      maxEnrollments: newSlab.maxEnrollments ? parseInt(newSlab.maxEnrollments) : null,
      rate: parseFloat(newSlab.rate),
      description: newSlab.description
    };
    
    const updatedSlabs = [...slabs, newSlabData].sort((a, b) => a.minEnrollments - b.minEnrollments);
    setSlabs(updatedSlabs);
    setIsAddingNew(false);
    setNewSlab({ minEnrollments: '', maxEnrollments: '', rate: '', description: '' });
    onSlabsChange?.(updatedSlabs);
  };

  const formatRange = (min, max) => {
    if (max === null) return `${min}+`;
    return `${min}-${max}`;
  };

  return (
    <Card className="neumorphic rounded-3xl">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg font-semibold text-neumorphic flex items-center gap-2">
            <Settings className="w-5 h-5 text-indigo-600" />
            Commission Slab Configuration
          </CardTitle>
          <Button
            onClick={() => setIsAddingNew(true)}
            className="neumorphic rounded-xl px-4 py-2 flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Add Slab
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Tier</TableHead>
              <TableHead>Enrollment Range</TableHead>
              <TableHead>Commission Rate</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {slabs.map((slab) => (
              <TableRow key={slab.id}>
                <TableCell>
                  {editingId === slab.id ? (
                    <Input
                      value={editForm.description}
                      onChange={(e) => setEditForm({...editForm, description: e.target.value})}
                      className="neumorphic-inset rounded-lg w-24"
                    />
                  ) : (
                    <Badge className="bg-indigo-100 text-indigo-800 border-0">
                      {slab.description}
                    </Badge>
                  )}
                </TableCell>
                <TableCell>
                  {editingId === slab.id ? (
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        value={editForm.minEnrollments}
                        onChange={(e) => setEditForm({...editForm, minEnrollments: e.target.value})}
                        className="neumorphic-inset rounded-lg w-16"
                      />
                      <span>to</span>
                      <Input
                        type="number"
                        value={editForm.maxEnrollments}
                        onChange={(e) => setEditForm({...editForm, maxEnrollments: e.target.value})}
                        placeholder="∞"
                        className="neumorphic-inset rounded-lg w-16"
                      />
                    </div>
                  ) : (
                    <span className="font-mono">{formatRange(slab.minEnrollments, slab.maxEnrollments)}</span>
                  )}
                </TableCell>
                <TableCell>
                  {editingId === slab.id ? (
                    <div className="flex items-center gap-1">
                      <Input
                        type="number"
                        step="0.1"
                        value={editForm.rate}
                        onChange={(e) => setEditForm({...editForm, rate: e.target.value})}
                        className="neumorphic-inset rounded-lg w-16"
                      />
                      <span>%</span>
                    </div>
                  ) : (
                    <span className="font-bold text-green-600">{slab.rate}%</span>
                  )}
                </TableCell>
                <TableCell>
                  {editingId === slab.id ? (
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        onClick={() => handleSave(slab.id)}
                        className="neumorphic rounded-lg p-2"
                      >
                        <Save className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => setEditingId(null)}
                        className="neumorphic rounded-lg p-2"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        onClick={() => handleEdit(slab)}
                        className="neumorphic rounded-lg p-2"
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => handleDelete(slab.id)}
                        className="neumorphic rounded-lg p-2 text-red-600"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </TableCell>
              </TableRow>
            ))}
            
            {isAddingNew && (
              <TableRow className="bg-blue-50">
                <TableCell>
                  <Input
                    value={newSlab.description}
                    onChange={(e) => setNewSlab({...newSlab, description: e.target.value})}
                    placeholder="Tier name"
                    className="neumorphic-inset rounded-lg"
                  />
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      value={newSlab.minEnrollments}
                      onChange={(e) => setNewSlab({...newSlab, minEnrollments: e.target.value})}
                      placeholder="Min"
                      className="neumorphic-inset rounded-lg w-16"
                    />
                    <span>to</span>
                    <Input
                      type="number"
                      value={newSlab.maxEnrollments}
                      onChange={(e) => setNewSlab({...newSlab, maxEnrollments: e.target.value})}
                      placeholder="Max (optional)"
                      className="neumorphic-inset rounded-lg w-20"
                    />
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-1">
                    <Input
                      type="number"
                      step="0.1"
                      value={newSlab.rate}
                      onChange={(e) => setNewSlab({...newSlab, rate: e.target.value})}
                      placeholder="Rate"
                      className="neumorphic-inset rounded-lg w-16"
                    />
                    <span>%</span>
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      onClick={handleAddNew}
                      className="neumorphic rounded-lg p-2 bg-green-500 text-white"
                    >
                      <Save className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => setIsAddingNew(false)}
                      className="neumorphic rounded-lg p-2"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}